import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Package, Clock, CheckCircle, KeyRound, Truck, LogOut, RefreshCw, Bell, BellOff, Volume2, VolumeX, Store, DollarSign, ShieldCheck, Settings as SettingsIcon, X, Search } from 'lucide-react';
import { AccountSettings } from '@/components/AccountSettings';

const STATUS_COLORS = { confirmed: 'border-blue-500 bg-blue-500/10', preparing: 'border-yellow-500 bg-yellow-500/10', ready: 'border-purple-500 bg-purple-500/10', out_for_delivery: 'border-amber-500 bg-amber-500/10' };

// Web Audio chime — two-tone alert, no asset needed
function playChime() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const play = (freq, start, dur) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, ctx.currentTime + start);
      gain.gain.linearRampToValueAtTime(0.25, ctx.currentTime + start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + dur + 0.05);
    };
    play(880, 0, 0.3);
    play(1175, 0.2, 0.35);
    setTimeout(() => ctx.close(), 900);
  } catch {}
}

export default function StaffPanel() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem('staff_sound') !== 'off');
  const [showAccount, setShowAccount] = useState(false);
  const lastOrderIdsRef = useRef(null);
  const firstLoadRef = useRef(true);

  useEffect(() => {
    if (!authLoading && (!user || !['admin', 'super_admin', 'staff'].includes(user.role))) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  const fetchOrders = useCallback(async () => {
    try {
      const res = await api.get('/staff/orders');
      const newOrders = res.data.orders || [];
      const newIds = new Set(newOrders.filter(o => o.status === 'confirmed').map(o => o.id));
      // Detect brand-new "confirmed" orders (skip the first fetch to avoid notifying on mount)
      if (!firstLoadRef.current && lastOrderIdsRef.current) {
        const fresh = [...newIds].filter(id => !lastOrderIdsRef.current.has(id));
        if (fresh.length > 0) {
          const firstFresh = newOrders.find(o => o.id === fresh[0]);
          toast.success(`New order: ${firstFresh?.order_number || ''}${fresh.length > 1 ? ` (+${fresh.length - 1} more)` : ''}`, { duration: 6000, position: 'top-center' });
          if (soundOn) playChime();
          // Browser tab notification (if permission given)
          try {
            if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
              new Notification(`New order ${firstFresh?.order_number || ''}`, { body: 'Check the kitchen board', silent: true });
            }
          } catch {}
          // Flash the tab title
          const orig = document.title;
          let count = 0;
          const blinker = setInterval(() => {
            document.title = count % 2 === 0 ? `(${fresh.length}) NEW ORDER` : orig;
            count++;
            if (count > 10) { clearInterval(blinker); document.title = orig; }
          }, 500);
        }
      }
      lastOrderIdsRef.current = newIds;
      firstLoadRef.current = false;
      setOrders(newOrders);
    } catch {} finally { setLoading(false); }
  }, [soundOn]);

  useEffect(() => {
    if (user) { fetchOrders(); const i = setInterval(fetchOrders, 10000); return () => clearInterval(i); }
  }, [user, fetchOrders]);

  const markReady = async (orderId) => {
    try { await api.put(`/staff/orders/${orderId}/ready`); toast.success('Order marked as ready!'); fetchOrders(); } catch { toast.error('Failed'); }
  };

  const completePickup = async (orderId, payload) => {
    try {
      await api.put(`/staff/orders/${orderId}/pickup-complete`, payload);
      toast.success('Pickup completed — order closed');
      fetchOrders();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to close pickup');
      return false;
    }
  };

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    localStorage.setItem('staff_sound', next ? 'on' : 'off');
    if (next) {
      playChime();
      toast.success('Order alerts ON');
      // Ask browser notification permission on first enable
      try { if (typeof Notification !== 'undefined' && Notification.permission === 'default') Notification.requestPermission(); } catch {}
    } else {
      toast.info('Order alerts muted');
    }
  };

  const [searchTerm, setSearchTerm] = useState('');
  const [fulfillmentFilter, setFulfillmentFilter] = useState('all');
  const [alcoholFilter, setAlcoholFilter] = useState('all');

  if (authLoading || !user) return <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#cc3737] border-t-transparent rounded-full animate-spin" /></div>;

  const filteredOrders = orders.filter(o => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = (o.order_number || '').toLowerCase().includes(term) ||
                          (o.customer_name || '').toLowerCase().includes(term) ||
                          (o.customer_phone || '').toLowerCase().includes(term);
    const matchesFulfillment = fulfillmentFilter === 'all' || o.fulfillment_type === fulfillmentFilter;
    const matchesAlcohol = alcoholFilter === 'all' || 
                           (alcoholFilter === 'alcohol' && o.has_alcohol) ||
                           (alcoholFilter === 'non_alcohol' && !o.has_alcohol);
    return matchesSearch && matchesFulfillment && matchesAlcohol;
  });

  const confirmed = filteredOrders.filter(o => o.status === 'confirmed');
  const preparing = filteredOrders.filter(o => o.status === 'preparing');
  const ready = filteredOrders.filter(o => o.status === 'ready');
  const outForDelivery = filteredOrders.filter(o => o.status === 'out_for_delivery');

  return (
    <div className="min-h-screen bg-[#0B0E14]" data-testid="staff-panel">
      <nav className="fixed top-0 w-full z-50 bg-[#0B0E14]/90 backdrop-blur-xl border-b border-white/10 h-14">
        <div className="h-full px-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#b56b38] flex items-center justify-center"><Package className="w-4 h-4 text-white" /></div>
            <span className="font-['Playfair_Display'] text-lg text-white">Kitchen / Store Staff</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={toggleSound} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${soundOn ? 'bg-[#10B981]/20 text-[#10B981] border border-[#10B981]/30' : 'bg-[#64748B]/20 text-[#64748B] border border-[#64748B]/30'}`} data-testid="toggle-sound-btn" title={soundOn ? 'Mute order alerts' : 'Enable order alerts'}>
              {soundOn ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
              <span className="hidden sm:inline">{soundOn ? 'Alerts On' : 'Muted'}</span>
            </button>
            <button onClick={fetchOrders} className="text-[#64748B] hover:text-white" data-testid="refresh-orders-btn"><RefreshCw className="w-4 h-4" /></button>
            <button onClick={() => setShowAccount(true)} className="text-[#64748B] hover:text-white" data-testid="staff-account-btn" title="Account settings"><SettingsIcon className="w-4 h-4" /></button>
            <span className="text-[#94A3B8] text-sm hidden md:inline">{user.name}</span>
            <button onClick={() => { logout(); navigate('/login'); }} className="text-[#64748B] hover:text-white" data-testid="staff-logout"><LogOut className="w-4 h-4" /></button>
          </div>
        </div>
      </nav>

      {/* Account Settings Drawer */}
      {showAccount && (
        <div className="fixed inset-0 z-50 flex items-start justify-end bg-black/60 backdrop-blur-sm p-4 pt-16" onClick={() => setShowAccount(false)} data-testid="staff-account-drawer">
          <div className="bg-[#0B0E14] border border-white/10 rounded-xl p-5 max-w-md w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-['Playfair_Display'] text-xl text-white">Account Settings</h3>
              <button onClick={() => setShowAccount(false)} className="text-[#64748B] hover:text-white" data-testid="close-staff-account"><X className="w-5 h-5" /></button>
            </div>
            <AccountSettings />
          </div>
        </div>
      )}

      <div className="pt-16 px-4 pb-6">
        {/* Stats */}
        <div className="grid grid-cols-4 gap-3 mb-6">
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-3 text-center"><p className="text-2xl font-bold text-blue-400">{confirmed.length}</p><p className="text-[#64748B] text-xs">New</p></div>
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3 text-center"><p className="text-2xl font-bold text-yellow-400">{preparing.length}</p><p className="text-[#64748B] text-xs">Preparing</p></div>
          <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-3 text-center"><p className="text-2xl font-bold text-purple-400">{ready.length}</p><p className="text-[#64748B] text-xs">Ready</p></div>
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 text-center"><p className="text-2xl font-bold text-amber-400">{outForDelivery.length}</p><p className="text-[#64748B] text-xs">Out</p></div>
        </div>

        {/* Search & Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6 bg-[#12151C] border border-white/10 rounded-xl p-3">
          <div className="relative">
            <Search className="w-4 h-4 text-[#64748B] absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search Order # or Customer..."
              className="w-full bg-[#0B0E14] border border-white/10 rounded-lg pl-9 pr-3 py-1.5 text-xs text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#b56b38]"
              data-testid="staff-order-search"
            />
          </div>
          <select
            value={fulfillmentFilter}
            onChange={e => setFulfillmentFilter(e.target.value)}
            className="bg-[#0B0E14] border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none"
            data-testid="staff-fulfillment-filter"
          >
            <option value="all">All Fulfillment</option>
            <option value="pickup">Pickup Only</option>
            <option value="delivery">Delivery Only</option>
          </select>
          <select
            value={alcoholFilter}
            onChange={e => setAlcoholFilter(e.target.value)}
            className="bg-[#0B0E14] border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none"
            data-testid="staff-alcohol-filter"
          >
            <option value="all">All Products</option>
            <option value="alcohol">Contains Alcohol (21+)</option>
            <option value="non_alcohol">Non-Alcoholic Only</option>
          </select>
        </div>

        {/* Orders Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* New Orders */}
          <div>
            <h3 className="text-blue-400 font-medium mb-3 flex items-center gap-2"><Bell className="w-4 h-4" /> New ({confirmed.length})</h3>
            <div className="space-y-3">
              {confirmed.map(order => <OrderCard key={order.id} order={order} onReady={markReady} onPickup={completePickup} onUpdateNotes={fetchOrders} />)}
            </div>
          </div>
          {/* Preparing */}
          <div>
            <h3 className="text-yellow-400 font-medium mb-3 flex items-center gap-2"><Clock className="w-4 h-4" /> Preparing ({preparing.length})</h3>
            <div className="space-y-3">
              {preparing.map(order => <OrderCard key={order.id} order={order} onReady={markReady} onPickup={completePickup} onUpdateNotes={fetchOrders} showOtp />)}
            </div>
          </div>
          {/* Ready */}
          <div>
            <h3 className="text-purple-400 font-medium mb-3 flex items-center gap-2"><CheckCircle className="w-4 h-4" /> Ready ({ready.length})</h3>
            <div className="space-y-3">
              {ready.map(order => <OrderCard key={order.id} order={order} onPickup={completePickup} onUpdateNotes={fetchOrders} showOtp />)}
            </div>
          </div>
          {/* Out for Delivery */}
          <div>
            <h3 className="text-amber-400 font-medium mb-3 flex items-center gap-2"><Truck className="w-4 h-4" /> Out ({outForDelivery.length})</h3>
            <div className="space-y-3">
              {outForDelivery.map(order => <OrderCard key={order.id} order={order} onUpdateNotes={fetchOrders} />)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function OrderCard({ order, onReady, onPickup, showOtp, onUpdateNotes }) {
  const [showNotesForm, setShowNotesForm] = useState(false);
  const [staffNote, setStaffNote] = useState(order.staff_note || '');
  const [otpInput, setOtpInput] = useState('');
  const [cashInput, setCashInput] = useState('');
  const [idConfirm, setIdConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const isPickup = order.fulfillment_type === 'pickup';
  const isCod = order.payment_method === 'cod';

  const submitPickup = async () => {
    if (!otpInput.trim() || otpInput.length !== 4) return toast.error('Enter the 4-digit pickup OTP');
    if (order.has_alcohol && !idConfirm) return toast.error('Verify customer 21+ ID first');
    if (isCod) {
      const amt = parseFloat(cashInput);
      if (!amt || amt < order.total) return toast.error(`Cash received must be at least $${order.total.toFixed(2)}`);
    }
    setSubmitting(true);
    const ok = await onPickup(order.id, {
      otp: otpInput,
      id_verified: !!idConfirm,
      cash_collected: isCod,
      cash_amount: isCod ? parseFloat(cashInput) : 0
    });
    if (ok) { setOtpInput(''); setCashInput(''); setIdConfirm(false); }
    setSubmitting(false);
  };

  return (
    <div className={`bg-[#12151C] border rounded-xl p-4 ${STATUS_COLORS[order.status] || 'border-white/10'}`} data-testid={`staff-order-${order.order_number}`}>
      <div className="flex justify-between items-center mb-2">
        <span className="text-[#b56b38] font-mono font-bold text-sm">{order.order_number}</span>
        <span className="text-[#64748B] text-xs">{new Date(order.created_at).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}</span>
      </div>
      {/* Badges */}
      <div className="flex gap-1 flex-wrap mb-2">
        {isPickup && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-[#b56b38]/20 text-[#b56b38] border border-[#b56b38]/30 flex items-center gap-1" data-testid={`badge-pickup-${order.order_number}`}><Store className="w-2.5 h-2.5" /> Pickup</span>}
        {!isPickup && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 flex items-center gap-1"><Truck className="w-2.5 h-2.5" /> Delivery</span>}
        {order.has_alcohol && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#EF4444]/20 text-[#EF4444]">21+</span>}
        {isCod && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#b56b38]/20 text-[#b56b38]">COD ${order.total?.toFixed(2)}</span>}
      </div>
      {/* Customer info for pickup orders */}
      {isPickup && order.customer_name && <p className="text-[#94A3B8] text-xs mb-2">Customer: <span className="text-white">{order.customer_name}</span>{order.customer_phone && ` · ${order.customer_phone}`}</p>}
      {/* Items */}
      <div className="space-y-1 mb-3">
        {order.items?.map((item, i) => (
          <div key={i} className="text-white font-medium text-sm">
            <span className="text-[#b56b38] font-bold">{item.quantity}x</span> {item.name}{item.variant_name ? ` (${item.variant_name})` : ''}
          </div>
        ))}
      </div>
      {order.notes && <p className="text-[#F59E0B] text-xs mb-2 bg-[#F59E0B]/10 rounded px-2 py-1">Customer Note: {order.notes}</p>}
      
      {/* Internal Staff Notes */}
      <div className="mt-2 bg-blue-500/5 border border-blue-500/20 rounded p-2 mb-2">
        <div className="flex justify-between items-center mb-1">
          <span className="text-[10px] text-[#94A3B8] font-semibold">INTERNAL STAFF NOTE</span>
          <button onClick={() => setShowNotesForm(!showNotesForm)} className="text-[10px] text-[#b56b38] hover:underline" data-testid={`edit-staff-note-${order.order_number}`}>{showNotesForm ? 'Cancel' : 'Edit'}</button>
        </div>
        {showNotesForm ? (
          <div className="space-y-1.5">
            <textarea
              value={staffNote}
              onChange={e => setStaffNote(e.target.value)}
              placeholder="Add internal kitchen/delivery instructions..."
              className="w-full bg-[#0B0E14] border border-white/10 rounded p-1.5 text-xs text-white resize-none"
              rows={2}
            />
            <button
              onClick={async () => {
                try {
                  await api.put(`/orders/${order.id}/staff-notes`, { staff_note: staffNote });
                  toast.success('Internal note saved');
                  setShowNotesForm(false);
                  if (onUpdateNotes) onUpdateNotes();
                } catch {
                  toast.error('Failed to save note');
                }
              }}
              className="bg-[#b56b38] hover:bg-[#c67c49] text-white px-3 py-1 rounded text-[10px] font-medium"
              data-testid={`save-staff-note-${order.order_number}`}
            >
              Save Note
            </button>
          </div>
        ) : (
          <p className="text-white text-xs">{order.staff_note || 'No internal notes.'}</p>
        )}
      </div>
      {/* Pickup OTP — display for driver (non-pickup orders) OR for reference on pickup */}
      {!isPickup && showOtp && order.pickup_otp && !order.pickup_otp_verified && (
        <div className="bg-[#b56b38]/20 border border-[#b56b38]/40 rounded-lg p-3 mb-2 text-center">
          <p className="text-[#94A3B8] text-xs">DRIVER PICKUP OTP</p>
          <p className="text-white text-3xl font-mono font-bold tracking-[0.3em]">{order.pickup_otp}</p>
        </div>
      )}
      {order.pickup_otp_verified && <p className="text-[#10B981] text-xs mb-2">Pickup verified</p>}
      {/* Mark Ready Button (for both delivery + pickup when preparing) */}
      {onReady && (order.status === 'confirmed' || order.status === 'preparing') && (
        <button onClick={() => onReady(order.id)} className="w-full bg-[#cc3737] hover:bg-[#d94444] text-white rounded-full py-2 text-sm font-medium transition-colors mb-2" data-testid={`mark-ready-${order.order_number}`}>
          Mark as Ready
        </button>
      )}
      {/* Customer Pickup Closure — only for pickup orders that are ready */}
      {isPickup && onPickup && (order.status === 'ready' || order.status === 'preparing') && (
        <div className="mt-2 pt-2 border-t border-white/5 space-y-2" data-testid={`pickup-close-${order.order_number}`}>
          <p className="text-white text-xs font-medium">Close pickup order:</p>
          <input value={otpInput} onChange={e => setOtpInput(e.target.value.replace(/\D/g, '').slice(0, 4))} placeholder="Customer's 4-digit code" maxLength={4} className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-2 py-1.5 text-sm text-white text-center font-mono tracking-[0.3em]" data-testid={`pickup-otp-input-${order.order_number}`} />
          {order.has_alcohol && (
            <label className="flex items-start gap-2 cursor-pointer bg-[#EF4444]/10 border border-[#EF4444]/30 rounded px-2 py-1.5">
              <input type="checkbox" checked={idConfirm} onChange={e => setIdConfirm(e.target.checked)} className="mt-0.5 accent-[#EF4444]" data-testid={`pickup-id-confirm-${order.order_number}`} />
              <span className="text-[10px] text-white flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-[#EF4444]" /> I verified customer's 21+ photo ID</span>
            </label>
          )}
          {isCod && (
            <div className="flex gap-1.5 items-center bg-[#b56b38]/10 border border-[#b56b38]/30 rounded px-2 py-1.5">
              <DollarSign className="w-3 h-3 text-[#b56b38] flex-shrink-0" />
              <input value={cashInput} onChange={e => setCashInput(e.target.value)} type="number" step="0.01" min={order.total} placeholder={`$${order.total?.toFixed(2)} min`} className="flex-1 bg-[#0B0E14] border border-white/10 rounded px-2 py-1 text-xs text-white font-mono" data-testid={`pickup-cash-input-${order.order_number}`} />
            </div>
          )}
          <button onClick={submitPickup} disabled={submitting} className="w-full bg-[#10B981] hover:bg-[#059669] disabled:opacity-40 text-white rounded-full py-1.5 text-xs font-medium" data-testid={`close-pickup-btn-${order.order_number}`}>
            {submitting ? 'Saving...' : 'Mark Picked Up'}
          </button>
        </div>
      )}
      {order.driver_name && <p className="text-[#94A3B8] text-xs mt-2">Driver: {order.driver_name}</p>}
    </div>
  );
}
